// Small utility to open a download URL reliably even when a browser's
// popup blocker intercepts window.open. Prefers a new tab; falls back to
// same-tab navigation so the user always ends up at the file instead of a
// silent "nothing happened" (which some browsers render as a page-not-found
// placeholder).

export function openDownload(url) {
  if (!url) return false;
  try {
    const w = window.open(url, "_blank", "noopener,noreferrer");
    if (w && !w.closed) return true;
    // Popup blocked or immediately closed — fall through to same-tab redirect.
  } catch {
    // window.open may throw in some sandboxed iframes.
  }
  window.location.href = url;
  return false;
}

/**
 * Open a set of download assets with popup-blocker resilience.
 *
 *   assets: [{ label, url }, ...]
 *   opts.onBlockedRest(remainingAssets) is called when subsequent tabs get
 *   blocked, so the caller can offer a manual fallback (e.g. a toast action).
 */
export function openDownloadAssets(assets, opts = {}) {
  if (!assets || assets.length === 0) return { openedInNewTab: false, blockedRest: [] };

  const first = assets[0];
  const rest = assets.slice(1);
  const primaryOpenedInNewTab = openDownload(first.url);

  // If the primary got blocked, the browser just navigated away — no point
  // trying to open the extras from this closed context.
  if (!primaryOpenedInNewTab) {
    if (rest.length > 0) opts.onBlockedRest?.(rest);
    return { openedInNewTab: false, blockedRest: rest };
  }

  const blocked = [];
  rest.forEach((asset, i) => {
    setTimeout(() => {
      try {
        const w = window.open(asset.url, "_blank", "noopener,noreferrer");
        if (!w || w.closed) blocked.push(asset);
      } catch {
        blocked.push(asset);
      }
      // On the last one, notify the caller if any got blocked.
      if (i === rest.length - 1 && blocked.length > 0) {
        opts.onBlockedRest?.(blocked);
      }
    }, (i + 1) * 200);
  });

  return { openedInNewTab: true, blockedRest: blocked };
}
